import { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { ESTADO_PUBLICADA } from '../../constants/publicaciones.js'
import { MAPA_PUBLICACIONES } from '../../helpers/filtros/mapaPublicaciones.js'
import { useAuth } from '../../hooks/useAuth'
import { resetCategorias, setReiniciaFiltros } from '../../slices/filterSlice.js'
import ComponenteError from '../publicaciones/ComponenteError.jsx'
import styles from '../publicaciones/Publicaciones.module.css'
import { FiltrosContainer } from '../reutilizables/filtros/FiltrosContainer.jsx'
import * as publicacionesService from '../servicios/publicacionesService.js'
import * as tiendasOficialesService from '../servicios/tiendasOficialesService.js'
import TiendaOficial from './TiendaOficial.jsx'
import tiendaStyles from './TiendasOficiales.module.css'

const TiendasOficiales = () => {
  const [tiendas, setTiendas] = useState([])
  const [textoBusqueda, setTextoBusqueda] = useState('')
  const [publicaciones, setPublicaciones] = useState([])
  const location = useLocation()
  const { state } = location
  const topRef = useRef(null)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { loginDataContext: { idUsuario, empid } = {} } = useAuth()
  const categoriaSeleccionada = useSelector((s) => s.filters.categoriaSeleccionada)
  const paginaFiltros = useSelector((s) => s.filters.paginaFiltros)

  const {
    tienda,
    idVendedor: idVendedorState,
    empid: empidState,
    vieneDeAdministrarTienda
  } = state || {}

  useEffect(() => {
    if (state?.vieneDeAdministrarTienda) {
      dispatch(resetCategorias())
      return
    }
    const vieneDeTiendasOficiales =
      paginaFiltros === 'TiendasOficiales' || paginaFiltros?.endsWith('TiendasOficiales')
    if (vieneDeTiendasOficiales && !state?.vieneConFiltroCategoria) {
      dispatch(setReiniciaFiltros(true))
    }
  }, [location.key])

  const buscarTiendas = useCallback(async () => {
    const { data } = await tiendasOficialesService.buscarTodas('')
    setTiendas(data.filter((t) => t.empid !== empid))
  }, [empid])

  const buscarPublicaciones = async () => {
    let data = []
    if (empidState) {
      const res = await publicacionesService.buscarPorEmpresa(empidState)
      data = res.data.body
    } else {
      const idVendedor = idVendedorState ?? 0
      const res = await publicacionesService.buscarTodasOptimizado(idUsuario, idVendedor, 0, '')
      data = res.data
    }

    const publicacionesEnTienda = data.filter(
      (p) => p.esTiendaOficial && p.idPublicacionEstado === ESTADO_PUBLICADA
    )
    setPublicaciones(publicacionesEnTienda)
  }

  useEffect(() => {
    buscarTiendas()
    buscarPublicaciones()
  }, [buscarTiendas])

  const navegarATienda = (empid, navState) => {
    if (!empid) {
      return
    }

    if (vieneDeAdministrarTienda) {
      dispatch(resetCategorias())
    } else if (categoriaSeleccionada) {
      navigate(location.pathname, {
        state: { ...state, vieneConFiltroCategoria: true },
        replace: true
      })
    }
    navigate(`/Publicaciones/TiendasOficiales/${empid}`, {
      state: {
        ...navState,
        empid
      }
    })
  }

  const tiendasFiltradas = tiendas.filter((t) => {
    const matchBusqueda =
      !textoBusqueda || t.nombreTienda?.toLowerCase().includes(textoBusqueda.toLowerCase())

    const matchCategoria =
      !categoriaSeleccionada ||
      publicaciones.some((p) => {
        const mismoVendedor =
          p.empid && t.empid ? p.empid === t.empid : Number(p.idVendedor) === Number(t.idUsuario)
        if (!mismoVendedor) return false
        try {
          const cats = JSON.parse(p.categoriaRoot).map((c) => c.id)
          return cats.includes(Number(categoriaSeleccionada))
        } catch {
          return false
        }
      })

    return matchBusqueda && matchCategoria
  })

  return (
    <div className={styles.layout} ref={topRef}>
      {!vieneDeAdministrarTienda && publicaciones.length > 0 && (
        <FiltrosContainer
          arrayAFiltrar={publicaciones}
          mapaFiltros={MAPA_PUBLICACIONES}
          filtrosVisibles={['categoria']}
          buscador={{
            onChange: () => {},
            placeholder: 'Buscar por nombre de tienda...',
            valor: textoBusqueda,
            setBusqueda: setTextoBusqueda
          }}
        />
      )}
      <fieldset className={`fieldset ${styles.fieldset}`}>
        <div className={styles.headerPublicaciones}>
          <legend>Tiendas Oficiales</legend>
        </div>
        {vieneDeAdministrarTienda ? (
          tienda?.idTiendaOficial ? (
            <div className={tiendaStyles.gridTiendas}>
              <TiendaOficial
                key={tienda.empid}
                tienda={tienda}
                onClick={() =>
                  navegarATienda(tienda.empid, {
                    vieneConFiltroCategoria: !!categoriaSeleccionada,
                    empid: tienda?.empid ?? empidState,
                    tiendaPropia: true
                  })
                }
              />
            </div>
          ) : (
            <div className="row">
              <ComponenteError tipo="I" idAviso={25} />
            </div>
          )
        ) : tiendasFiltradas.length > 0 ? (
          <div className={tiendaStyles.gridTiendas}>
            {tiendasFiltradas.map((tienda) => (
              <TiendaOficial
                key={tienda.empid}
                tienda={tienda}
                onClick={() =>
                  navegarATienda(tienda.empid, {
                    vieneConFiltroCategoria: !!categoriaSeleccionada,
                    empid: tienda.empid,
                    tiendaPropia: false
                  })
                }
              />
            ))}
          </div>
        ) : (
          <div className="row">
            <ComponenteError tipo="I" idAviso={25} />
          </div>
        )}
      </fieldset>
    </div>
  )
}

export default TiendasOficiales
